import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, ChevronRight, Gift, X, Check, Ban } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

/**
 * Admin — Codes Créateurs (JEANMARC & Cie).
 *
 * Section entièrement séparée du système de parrainage user-to-user :
 *   1. CRUD des codes avec paramètres personnalisables (bonus wallet, commission
 *      par course, nombre max de courses commissionnées, activation).
 *   2. Statistiques agrégées par code (inscriptions, users actifs,
 *      commissions générées / payées / dues).
 *   3. Fiche par code : liste des utilisateurs rattachés + commissions détaillées.
 *   4. Bouton "Marquer payé" (idempotent, aucun virement Stripe).
 *
 * Aucune modification du système Stripe / wallet existant. Tout passe par
 * les endpoints `/admin/price/creator-codes*`.
 */
export default function CreatorCodesTab() {
  const [codes, setCodes] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/price/creator-codes");
      setCodes(data?.codes || []);
    } catch (err) {
      toast.error(formatApiError(err));
      setCodes([]);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-3">
      <div className="rounded-2xl bg-white p-4 street-border street-shadow">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-primary text-primary-foreground street-border-thin">
            <Gift className="h-5 w-5" strokeWidth={2.5} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-[Outfit] text-[15px] font-black leading-tight">
              Codes Créateurs
            </div>
            <div className="mt-0.5 text-[11px] font-medium text-foreground/60">
              JEANMARC & partenaires — bonus wallet + commissions manuelles
            </div>
          </div>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            data-testid="creator-codes-new-btn"
            className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-[12px] font-black text-primary-foreground street-border-thin street-press"
          >
            <Plus className="h-4 w-4" /> Nouveau
          </button>
        </div>
      </div>

      {codes === null && (
        <div className="grid place-items-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      )}

      {codes && codes.length === 0 && (
        <div className="rounded-2xl bg-white p-6 text-center street-border-thin">
          <div className="text-[13px] font-black text-foreground/70">Aucun code créateur</div>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            data-testid="creator-codes-empty-cta"
            className="mt-3 inline-flex items-center gap-1 rounded-lg bg-primary px-4 py-2 text-[12px] font-black text-primary-foreground street-border-thin street-press"
          >
            <Plus className="h-4 w-4" /> Créer le premier
          </button>
        </div>
      )}

      {codes && codes.length > 0 && (
        <div className="space-y-2">
          {codes.map((c) => (
            <CreatorCodeRow
              key={c.id}
              c={c}
              onOpen={() => setDetailId(c.id)}
              testid={`creator-code-row-${c.code}`}
            />
          ))}
        </div>
      )}

      {createOpen && (
        <CreatorCodeCreateModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); load(); }}
        />
      )}
      {detailId && (
        <CreatorCodeDetailModal
          codeId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function CreatorCodeRow({ c, onOpen, testid }) {
  const stats = c.stats || {};
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={testid}
      className="w-full text-left rounded-2xl bg-white p-3 street-border-thin street-shadow-sm street-press"
    >
      <div className="flex items-center gap-3">
        <div className={`h-2.5 w-2.5 flex-none rounded-full ${c.active ? "bg-emerald-500" : "bg-neutral-400"}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-[Outfit] text-[15px] font-black tracking-wider">{c.code}</span>
            <span className="text-[11px] font-medium text-foreground/60 truncate">— {c.owner_name}</span>
          </div>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-foreground/70">
            <span><b>{stats.signups_count || 0}</b> inscrits</span>
            <span><b>{stats.active_users_count || 0}</b> actifs</span>
            <span><b>{stats.eligible_rides_count || 0}</b> courses</span>
            <span className="text-primary"><b>{(stats.outstanding_eur || 0).toFixed(2)}€</b> à régler</span>
          </div>
        </div>
        <ChevronRight className="h-4 w-4 text-foreground/40 flex-none" />
      </div>
    </button>
  );
}

function CreatorCodeCreateModal({ onClose, onCreated }) {
  const [f, setF] = useState({
    code: "",
    owner_name: "",
    owner_email: "",
    wallet_bonus_eur: 10,
    commission_eur_per_ride: 1,
    max_commissioned_rides: 3,
    active: true,
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async () => {
    if (busy) return;
    const code = (f.code || "").trim().toUpperCase();
    if (!code || !/^[A-Z0-9]{2,32}$/.test(code)) return toast.error("Code : lettres/chiffres uniquement, 2 à 32 caractères");
    if (!f.owner_name.trim()) return toast.error("Nom du créateur obligatoire");
    setBusy(true);
    try {
      await api.post("/admin/price/creator-codes", {
        code,
        owner_name: f.owner_name.trim(),
        owner_email: f.owner_email.trim() || undefined,
        wallet_bonus_eur: Number(f.wallet_bonus_eur),
        commission_eur_per_ride: Number(f.commission_eur_per_ride),
        max_commissioned_rides: Number(f.max_commissioned_rides),
        active: !!f.active,
      });
      toast.success(`✅ Code ${code} créé`);
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Nouveau code créateur" onClose={onClose} testid="creator-code-create-modal">
      <div className="space-y-3">
        <FieldRow label="Code (unique, uppercase)">
          <input value={f.code} onChange={(e) => setF((s) => ({ ...s, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 32) }))} placeholder="JEANMARC" className="input" data-testid="creator-code-input" />
        </FieldRow>
        <FieldRow label="Nom du créateur">
          <input value={f.owner_name} onChange={set("owner_name")} placeholder="Jean-Marc" className="input" data-testid="creator-owner-input" />
        </FieldRow>
        <FieldRow label="Email (optionnel — pour tes règlements manuels)">
          <input type="email" value={f.owner_email} onChange={set("owner_email")} placeholder="jeanmarc@..." className="input" />
        </FieldRow>
        <div className="grid grid-cols-3 gap-2">
          <FieldRow label="Bonus wallet (€)">
            <input type="number" step="1" min="0" value={f.wallet_bonus_eur} onChange={set("wallet_bonus_eur")} className="input" data-testid="creator-bonus-input" />
          </FieldRow>
          <FieldRow label="Commission / course (€)">
            <input type="number" step="0.5" min="0" value={f.commission_eur_per_ride} onChange={set("commission_eur_per_ride")} className="input" data-testid="creator-commission-input" />
          </FieldRow>
          <FieldRow label="Nb courses max">
            <input type="number" step="1" min="1" value={f.max_commissioned_rides} onChange={set("max_commissioned_rides")} className="input" data-testid="creator-max-rides-input" />
          </FieldRow>
        </div>
        <label className="inline-flex items-center gap-2 text-[12px] font-bold text-foreground/70">
          <input type="checkbox" checked={f.active} onChange={(e) => setF((s) => ({ ...s, active: e.target.checked }))} data-testid="creator-active-input" />
          Actif immédiatement
        </label>
        <div className="rounded-xl bg-neutral-50 street-border-thin p-3 text-[11px] text-foreground/70">
          💡 Aucun virement automatique. Les commissions sont enregistrées côté back-office —
          tu règles le créateur sur facture puis clique "Marquer payé" dans la fiche du code.
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="rounded-lg bg-neutral-200 px-3 py-2 text-[12px] font-black text-neutral-900 street-border-thin street-press">Annuler</button>
        <button onClick={submit} disabled={busy} data-testid="creator-code-create-submit" className="inline-flex items-center gap-1 rounded-lg bg-primary px-4 py-2 text-[13px] font-black text-primary-foreground street-border-thin street-press disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Créer
        </button>
      </div>
    </ModalShell>
  );
}

function CreatorCodeDetailModal({ codeId, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const [payNote, setPayNote] = useState("");
  const [showPayNote, setShowPayNote] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/price/creator-codes/${codeId}`);
      setD(data);
    } catch (err) {
      toast.error(formatApiError(err));
      onClose();
    }
  };
  useEffect(() => { load(); }, [codeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleActive = async () => {
    if (busy) return;
    setBusy("toggle");
    try {
      const { data } = await api.patch(`/admin/price/creator-codes/${codeId}`, { active: !d.active });
      setD(data);
      onChanged && onChanged();
      toast.success(data.active ? "Code activé" : "Code désactivé");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(""); }
  };

  const markPaid = async () => {
    if (busy) return;
    setBusy("mark-paid");
    try {
      const { data } = await api.post(`/admin/price/creator-codes/${codeId}/mark-paid`, {
        note: payNote.trim() || undefined,
      });
      toast.success(`✅ ${data.marked_count} commission${data.marked_count > 1 ? "s" : ""} marquée${data.marked_count > 1 ? "s" : ""} payée${data.marked_count > 1 ? "s" : ""}`);
      setPayNote(""); setShowPayNote(false);
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(""); }
  };

  if (!d) {
    return (
      <ModalShell title="Chargement…" onClose={onClose}>
        <div className="grid place-items-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      </ModalShell>
    );
  }

  const stats = d.stats || {};
  const outstanding = stats.outstanding_eur || 0;

  return (
    <ModalShell
      title={`Code ${d.code}`}
      onClose={onClose}
      testid="creator-code-detail-modal"
      right={
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-widest street-border-thin ${d.active ? "bg-emerald-200 text-emerald-900" : "bg-neutral-200 text-neutral-700"}`}>
          {d.active ? "Actif" : "Désactivé"}
        </span>
      }
    >
      <div className="space-y-4">
        <div className="rounded-xl bg-neutral-50 street-border-thin p-3 text-[12px]">
          <div><b>{d.owner_name}</b>{d.owner_email && <span className="text-foreground/60"> · {d.owner_email}</span>}</div>
          <div className="mt-1 text-foreground/60">
            {d.wallet_bonus_eur}€ offert / {d.commission_eur_per_ride}€ par course / max {d.max_commissioned_rides} courses par user
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2">
          <StatBox label="Inscrits" value={stats.signups_count || 0} />
          <StatBox label="Actifs" value={stats.active_users_count || 0} />
          <StatBox label="Courses" value={stats.eligible_rides_count || 0} />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <StatBox label="Générées" value={`${(stats.total_earned_eur || 0).toFixed(2)}€`} tone="primary" />
          <StatBox label="Payées" value={`${(stats.total_paid_eur || 0).toFixed(2)}€`} tone="ok" />
          <StatBox label="À régler" value={`${outstanding.toFixed(2)}€`} tone={outstanding > 0 ? "warn" : "muted"} />
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={toggleActive}
            disabled={busy === "toggle"}
            data-testid="creator-code-toggle-active"
            className={`inline-flex items-center gap-1 rounded-lg px-3 py-2 text-[12px] font-black street-border-thin street-press disabled:opacity-50 ${d.active ? "bg-neutral-200 text-neutral-900" : "bg-emerald-500 text-white"}`}
          >
            {d.active ? <Ban className="h-4 w-4" /> : <Check className="h-4 w-4" />}
            {d.active ? "Désactiver" : "Activer"}
          </button>
          {outstanding > 0 && !showPayNote && (
            <button
              type="button"
              onClick={() => setShowPayNote(true)}
              data-testid="creator-code-mark-paid-open"
              className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-[12px] font-black text-primary-foreground street-border-thin street-press"
            >
              💶 Marquer {outstanding.toFixed(2)}€ payés
            </button>
          )}
        </div>

        {showPayNote && (
          <div className="rounded-xl bg-yellow-50 street-border-thin p-3 space-y-2">
            <div className="text-[11px] font-black text-yellow-900">
              ⚠️ Cette action marque toutes les commissions non-payées comme réglées.
              Aucun virement n'est effectué — tu dois régler {d.owner_name} manuellement.
            </div>
            <input
              value={payNote}
              onChange={(e) => setPayNote(e.target.value)}
              placeholder="Référence virement / note (optionnel)"
              className="input"
              data-testid="creator-code-pay-note-input"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => { setShowPayNote(false); setPayNote(""); }} className="rounded-lg bg-neutral-200 px-3 py-1.5 text-[12px] font-black street-border-thin street-press">Annuler</button>
              <button onClick={markPaid} disabled={busy === "mark-paid"} data-testid="creator-code-mark-paid-confirm" className="rounded-lg bg-primary px-4 py-1.5 text-[12px] font-black text-primary-foreground street-border-thin street-press disabled:opacity-50">
                {busy === "mark-paid" ? "…" : "Confirmer"}
              </button>
            </div>
          </div>
        )}

        {/* Users rattachés */}
        <div>
          <div className="text-[11px] font-black uppercase tracking-widest text-foreground/60 mb-2">
            Utilisateurs rattachés ({(d.users || []).length})
          </div>
          <div className="space-y-1 max-h-80 overflow-y-auto">
            {(d.users || []).length === 0 && (
              <div className="rounded-lg bg-neutral-50 street-border-thin p-3 text-center text-[11px] text-foreground/50">
                Aucun utilisateur pour l'instant
              </div>
            )}
            {(d.users || []).map((u) => (
              <div key={u.id} className="rounded-lg bg-white street-border-thin p-2 text-[11px]">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-black text-[12px] truncate">{u.email}</div>
                    <div className="mt-0.5 text-foreground/60">
                      {u.role} · {(u.creator_code_eligible_rides_count || 0)} courses éligibles · inscrit le {(u.created_at || "").slice(0, 10)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono font-black">{(u.commissions_total_eur || 0).toFixed(2)}€</div>
                    {u.commissions_paid_eur > 0 && (
                      <div className="text-[10px] text-emerald-700">payé: {u.commissions_paid_eur.toFixed(2)}€</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Commissions log */}
        <div>
          <div className="text-[11px] font-black uppercase tracking-widest text-foreground/60 mb-2">
            Commissions ({(d.commissions || []).length})
          </div>
          <div className="space-y-1 max-h-80 overflow-y-auto">
            {(d.commissions || []).length === 0 && (
              <div className="rounded-lg bg-neutral-50 street-border-thin p-3 text-center text-[11px] text-foreground/50">
                Aucune commission générée
              </div>
            )}
            {(d.commissions || []).map((c) => (
              <div key={c.id} className={`rounded-lg street-border-thin p-2 text-[11px] ${c.paid_at ? "bg-emerald-50" : "bg-white"}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-[10px] truncate">{c.user_email || c.user_id}</span>
                  <span className="font-mono font-black">{c.amount_eur.toFixed(2)}€</span>
                </div>
                <div className="mt-0.5 text-foreground/60">
                  Course #{c.ride_index} · {c.ride_kind} · {(c.created_at || "").slice(0, 16).replace("T", " ")}
                  {c.paid_at && <span className="text-emerald-700"> · payée le {(c.paid_at || "").slice(0, 10)}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </ModalShell>
  );
}

function StatBox({ label, value, tone }) {
  const bg = tone === "primary" ? "bg-primary/10" : tone === "ok" ? "bg-emerald-50" : tone === "warn" ? "bg-yellow-50" : "bg-neutral-50";
  return (
    <div className={`rounded-xl street-border-thin p-2.5 ${bg}`}>
      <div className="text-[10px] font-black uppercase tracking-widest text-foreground/60">{label}</div>
      <div className="mt-1 font-mono text-[16px] font-black leading-none">{value}</div>
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-black uppercase tracking-widest text-foreground/60">{label}</span>
      {children}
    </label>
  );
}

function ModalShell({ title, onClose, children, right, testid }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4" data-testid={testid}>
      <div className="w-full max-w-lg rounded-t-3xl sm:rounded-3xl bg-neutral-50 max-h-[92vh] overflow-y-auto street-border street-shadow">
        <div className="sticky top-0 z-10 flex items-center justify-between gap-2 bg-neutral-50 px-4 py-3 border-b border-black/10">
          <div className="min-w-0 flex-1">
            <div className="font-[Outfit] text-[16px] font-black leading-tight truncate">{title}</div>
            {right && <div className="mt-1">{right}</div>}
          </div>
          <button type="button" onClick={onClose} className="grid h-9 w-9 flex-none place-items-center rounded-full bg-white street-border-thin street-press" aria-label="Fermer">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
